import { useQuery } from '@tanstack/react-query'

import { useGlobalState } from './useGlobalState'

/**
 * Returns whether the currently authenticated user is a SWI Admin.
 *
 * Reads `CheckUserResponse.isSwiAdmin`. Falls back to a one-time
 * `/members/get` self-lookup only when the field is missing from the
 * auth payload (older backends); errors are treated as "not a SWI Admin".
 */
export function useIsSwiAdmin(): boolean {
  const { api, authenticationStore } = useGlobalState()
  const user = authenticationStore.user
  const fromAuth = user?.isSwiAdmin

  const fallbackQuery = useQuery({
    queryKey: ['users', 'is-swi-admin', user?.userId ?? null],
    queryFn: async () => {
      if (!user?.email) return false
      try {
        const response = await api.getUsers({
          page: 1,
          pageSize: 1,
          role: 'SWI Admin',
          filterBy: user.email,
        })
        return response.members.some(
          member => member.email.toLowerCase() === user.email.toLowerCase() && member.isSwiAdmin,
        )
      } catch {
        return false
      }
    },
    enabled: fromAuth === undefined && !!user?.userId && !!user?.email,
    staleTime: Infinity,
    retry: false,
  })

  if (fromAuth === true) return true
  if (fromAuth === false) return false
  return fallbackQuery.data === true
}
